import { action, makeObservable, observable, runInAction } from "mobx";
import { inject, injectable } from "inversify";
import IdentityObject from "@/src/domain/common/identity-object";
import AlarmService from "@/src/application/services/alarm-service";
import type Alarm from "@/src/domain/models/alarm";

@injectable()
export default class AlarmStore extends IdentityObject {
  summary: Alarm[] = [];
  activeCount: number = 0;
  lastUpdated: number = 0;

  constructor(@inject(AlarmService) private readonly alarmService: AlarmService) {
    super();
    makeObservable(this, {
      summary: observable,
      activeCount: observable,
      lastUpdated: observable,
      fetchSummary: action,
      fetchCount: action,
      markAsRead: action,
      markAllAsRead: action,
    });
  }

  async fetchSummary(force = false) {
    // Cache for 10 seconds unless forced
    if (!force && Date.now() - this.lastUpdated < 10000 && this.summary.length > 0) {
      return;
    }

    try {
      const data = await this.alarmService.fetchActiveAlarmSummary();
      runInAction(() => {
        this.summary = data.sort((a, b) => b.time.diff(a.time, "milliseconds").milliseconds);
        this.lastUpdated = Date.now();
      });
    } catch (e) {
      console.error("Failed to fetch alarm summary in store", e);
    }
  }

  async fetchCount() {
    try {
      const count = await this.alarmService.fetchActiveAlarmCount();
      runInAction(() => {
        this.activeCount = count;
      });
    } catch (e) {
      console.error("Failed to fetch active alarm count in store", e);
    }
  }

  async markAsRead(id: string) {
    try {
      await this.alarmService.markAlarmAsRead(id);
      runInAction(() => {
        const alarm = this.summary.find((a) => a.id === id);
        if (alarm && !alarm.isRead) {
          alarm.isRead = true;
        }
      });
    } catch (e) {
      console.error("Failed to mark alarm as read in store", e);
    }
  }

  async markAllAsRead() {
    try {
      await this.alarmService.markAllAsRead();
      runInAction(() => {
        this.summary.forEach((a) => {
          a.isRead = true;
        });
      });
    } catch (e) {
      console.error("Failed to mark all as read in store", e);
    }
  }
}
